import { useState } from 'react';
import { useTranslation } from '../../i18n';
import { api } from '../../lib/api';
import { PhotoUpload } from '../../components/PhotoUpload';
import { AddressInput, emptyAddress } from '../../components/AddressInput';
import { stringifyAddress } from '../../lib/formatters';

const ORG_TYPES = ['partner', 'funder', 'government', 'ngo', 'university', 'media', 'corporate', 'other'];

// Reusable add-organisation modal. The inline edit form in OrganisationsList
// shares the same field shape but uses a different submit path (PATCH vs POST).
export function CreateOrganisationModal({ onClose, onCreated }) {
  const { t } = useTranslation();
  const [logoB64, setLogoB64] = useState(null);
  const [name, setName] = useState('');
  const [type, setType] = useState('partner');
  const [description, setDescription] = useState('');
  const [website, setWebsite] = useState('');
  const [emails, setEmails] = useState(['']);
  const [phones, setPhones] = useState(['']);
  const [address, setAddress] = useState(emptyAddress());
  const [linkedin, setLinkedin] = useState('');
  const [instagram, setInstagram] = useState('');
  const [facebook, setFacebook] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const trimmedName = name.trim();
  const canSubmit = trimmedName.length > 0 && !submitting;

  function buildSocialHandles() {
    const handles = {};
    if (linkedin.trim())  handles.linkedin  = linkedin.trim();
    if (instagram.trim()) handles.instagram = instagram.trim();
    if (facebook.trim())  handles.facebook  = facebook.trim();
    return handles;
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (!canSubmit) return;
    setError(null);
    setSubmitting(true);
    try {
      const created = await api.organisations.create({
        name: trimmedName,
        type,
        description: description.trim() || null,
        logoB64: logoB64 || null,
        website: website.trim() || null,
        emails: emails.map((e) => e.trim()).filter(Boolean),
        phones: phones.map((p) => p.trim()).filter(Boolean),
        address: stringifyAddress(address),
        socialHandles: buildSocialHandles(),
        notes: notes.trim() || null,
      });
      onCreated?.(created);
      onClose();
    } catch (err) {
      setError(err?.message || t('organisations.form.error'));
    } finally {
      setSubmitting(false);
    }
  }

  function handleBackdropClick() {
    if (!submitting) onClose();
  }

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" onClick={handleBackdropClick}>
      <form
        className="modal modal--form"
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
        noValidate
      >
        <h2 className="modal__title">{t('organisations.form.createTitle')}</h2>

        {/* ── Identity ────────────────────────────────────────────────── */}
        <div className="form-section">
          <h3 className="form-section__title">{t('organisations.form.sections.identity')}</h3>

          <div className="person-photo-row">
            <PhotoUpload value={logoB64} onChange={setLogoB64} shape="square" size={80} />
            <label className="form-field person-photo-row__field">
              <span className="form-field__label">{t('organisations.form.name')} *</span>
              <input
                className="form-field__input"
                type="text"
                value={name}
                placeholder={t('organisations.form.namePlaceholder')}
                onChange={(e) => setName(e.target.value)}
                autoFocus
                required
              />
            </label>
          </div>

          <label className="form-field">
            <span className="form-field__label">{t('organisations.form.type')}</span>
            <select
              className="form-field__select"
              value={type}
              onChange={(e) => setType(e.target.value)}
            >
              {ORG_TYPES.map((typeKey) => (
                <option key={typeKey} value={typeKey}>{t(`organisations.types.${typeKey}`)}</option>
              ))}
            </select>
          </label>

          <label className="form-field">
            <span className="form-field__label">{t('organisations.form.description')}</span>
            <textarea
              className="form-field__textarea"
              value={description}
              placeholder={t('organisations.form.descriptionPlaceholder')}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
            />
          </label>
        </div>

        {/* ── Contact ─────────────────────────────────────────────────── */}
        <div className="form-section">
          <h3 className="form-section__title">{t('organisations.form.sections.contact')}</h3>

          <label className="form-field">
            <span className="form-field__label">{t('organisations.form.website')}</span>
            <input
              className="form-field__input"
              type="url"
              value={website}
              placeholder={t('organisations.form.websitePlaceholder')}
              onChange={(e) => setWebsite(e.target.value)}
            />
          </label>

          <MultiInput
            labelKey="organisations.form.emails"
            placeholderKey="organisations.form.emailPlaceholder"
            addLabelKey="organisations.form.addEmail"
            type="email"
            values={emails}
            onChange={setEmails}
          />

          <MultiInput
            labelKey="organisations.form.phones"
            placeholderKey="organisations.form.phonePlaceholder"
            addLabelKey="organisations.form.addPhone"
            type="tel"
            values={phones}
            onChange={setPhones}
          />

          <div className="form-field">
            <span className="form-field__label">{t('organisations.form.address')}</span>
            <AddressInput value={address} onChange={setAddress} />
          </div>
        </div>

        {/* ── Social ──────────────────────────────────────────────────── */}
        <div className="form-section">
          <h3 className="form-section__title">{t('organisations.form.sections.social')}</h3>
          <div className="form-field__stack">
            <div className="social-input">
              <span className="social-input__icon" aria-hidden="true">💼</span>
              <input
                className="form-field__input"
                type="text"
                value={linkedin}
                placeholder={t('organisations.form.linkedinPlaceholder')}
                onChange={(e) => setLinkedin(e.target.value)}
              />
            </div>
            <div className="social-input">
              <span className="social-input__icon" aria-hidden="true">📷</span>
              <input
                className="form-field__input"
                type="text"
                value={instagram}
                placeholder={t('organisations.form.instagramPlaceholder')}
                onChange={(e) => setInstagram(e.target.value)}
              />
            </div>
            <div className="social-input">
              <span className="social-input__icon" aria-hidden="true">👍</span>
              <input
                className="form-field__input"
                type="text"
                value={facebook}
                placeholder={t('organisations.form.facebookPlaceholder')}
                onChange={(e) => setFacebook(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* ── Context ─────────────────────────────────────────────────── */}
        <div className="form-section">
          <h3 className="form-section__title">{t('organisations.form.sections.context')}</h3>
          <label className="form-field">
            <span className="form-field__label">{t('organisations.form.notes')}</span>
            <textarea
              className="form-field__textarea"
              value={notes}
              placeholder={t('organisations.form.notesPlaceholder')}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
          </label>
        </div>

        {error && <div className="modal__error" role="alert">{error}</div>}

        <div className="modal__actions">
          <button
            type="button"
            className="button button--ghost"
            onClick={onClose}
            disabled={submitting}
          >
            {t('common.cancel')}
          </button>
          <button type="submit" className="button button--primary" disabled={!canSubmit}>
            {submitting ? t('organisations.form.submitting') : t('organisations.form.submitCreate')}
          </button>
        </div>
      </form>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MultiInput — used for emails + phones. Always renders at least one row.
// ─────────────────────────────────────────────────────────────────────────────
export function MultiInput({ labelKey, placeholderKey, addLabelKey, type = 'text', values, onChange }) {
  const { t } = useTranslation();

  function update(index, value) {
    const next = [...values];
    next[index] = value;
    onChange(next);
  }
  function add() { onChange([...values, '']); }
  function remove(index) {
    if (values.length <= 1) onChange(['']);
    else onChange(values.filter((_, i) => i !== index));
  }

  return (
    <div className="form-field">
      <span className="form-field__label">{t(labelKey)}</span>
      <div className="multi-input">
        {values.map((value, i) => (
          <div key={i} className="multi-input__row">
            <input
              className="form-field__input"
              type={type}
              value={value}
              placeholder={t(placeholderKey)}
              onChange={(e) => update(i, e.target.value)}
            />
            {(values.length > 1 || values[0]) && (
              <button
                type="button"
                className="multi-input__remove"
                onClick={() => remove(i)}
                aria-label={t('common.remove')}
              >
                ×
              </button>
            )}
          </div>
        ))}
        <button type="button" className="multi-input__add" onClick={add}>
          ＋ {t(addLabelKey)}
        </button>
      </div>
    </div>
  );
}
